import { REFERRAL_OBSERVATION_CODE } from 'components/common/referrals';
import { VENDOR_MAP } from 'components/common/vendorConfig';
import { useAuthContext } from 'contexts';
import { ConnectHooksFetcher } from 'data/ConnectFetcher';
import FHIRFetcher from 'data/FHIRFetcher';
import type { RevalidateTrigger } from 'data/FHIRFetcher';
import HooksPoster from 'data/HooksPoster';
import type { Bundle, Flag, Identifier, Patient, Provenance, Task } from 'fhir/r4';
import { FHIRProxyClient, FHIRUtils, FlagBuilder, ProvenanceBuilder } from 'fhir-sdoh';
import * as fhirpath from 'fhirpath';
import PatientCDO from 'models/patient';
import type { PatientCDOModel } from 'models/patient';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Loader, Dimmer, Container, Grid, Segment } from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { FlatPractitioner, Option } from 'types';
import * as Helpers from 'utils';
import * as FHIRUtility from 'utils/FHIRUtils';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import { ConsultCancelDueDateWarning } from '../taskHeader/assignTask/consultCancelDueDateWarning';
import { ConsultInProgressWarning } from '../taskHeader/assignTask/consultInProgressWarning';
import {
  applyConsultMemberEventDueDate,
  isAutomatedTask,
  isConsultationTask,
  isFfsMntConsultationTask,
  isFoodDeliveryTask,
  needsConsultCancelDueDateConfirmSingle,
  needsConsultInProgressConfirmSingle,
} from '../taskHeader/assignTask/hooks/utils';
import type { DefaultOptionType } from '../taskHeader/types';

import TaskDetailsForm from './form';
// Import the new components
import TaskHeader from './header';
import TaskDetailTabs from './tabs';

/**
 * The `/hooks/fetch-voice-mail-audio/{contactId}` reply, as this file reads it.
 *
 * `ConnectHooksFetcher` hands back a raw `AxiosResponse`, whose `data` is `any`;
 * the two consumers here treat that payload as a presigned audio URL, and test it
 * against `null` to mean the lookup failed. Naming those two states is what keeps
 * `vmURL` and the error branch out of `any`.
 */
type VoiceMailAudioResponse = { data?: string | null };

/** `fhirProxyClient.send` rejects with the axios error, whose `toJSON()` carries the message. */
type FhirSendRejection = { toJSON: () => { message: string } };

type TaskDetailsProps = {
  /** `null` while no task is selected; the panel renders its empty state instead. */
  taskId: string | null;
  /**
   * Only `.length` is read, and only to decide whether to render the detail pane —
   * no element field is touched. Typed as the sibling list it is passed, but kept
   * to the read that actually happens rather than the full `Task[]`.
   */
  taskList: readonly { id?: string | undefined }[] | null;
  render: boolean;
  flatPractitioners: FlatPractitioner[];
  practlist: DefaultOptionType[];
  foodVendor: Option[];
  activity?: boolean | undefined;
  patientId?: string | undefined;
  /** Absent on the per-member panel, where selecting a patient is not offered. */
  onPatientSelected?: ((patientData: unknown) => void) | undefined;
  tasksMutate: RevalidateTrigger;
  /** SWR mutate for `GET /Task?patient=…` on the patient shell; keeps Surveys / PW-OTHER-13 in sync after care plan edits */
  patientTasksMutate?: (() => void | Promise<unknown>) | undefined;
  setPreviousURL: (url: string | null) => void;
  setRenderURL: (value: boolean) => void;
};

const TaskDetails: React.FC<TaskDetailsProps> = (props) => {
  const flatPractitioners = props.flatPractitioners;
  const authInfo = useAuthContext();
  const [loader, setLoader] = useState(false);
  // Set from `Task.groupIdentifier.value`, which is `0..1` in FHIR, with `''` when
  // the task carries no groupIdentifier at all.
  const [referralId, setReferralId] = useState<string | null | undefined>(null);
  const [orderId, setOrderId] = useState('');
  const [foodRecommendationNotes, setFoodRecommendationNotes] = useState('');
  const [clipBoard, setClipBoard] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [audioError, setAudioError] = useState('');
  /*
   * Seeded with the empty `PatientCDO` record, then replaced by `parseFHIRPatient`'s
   * output once the `/Patient/{id}` fetch resolves.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Two types describe the same flattened
   * patient and neither is assignable to the other. `PatientCDOModel` (the seed)
   * declares the fhirpath-derived fields as `string[]` and `id` as `string`;
   * `FlatPatient` (what the setter is actually given) has them as `unknown[]` and
   * `string | undefined`, because `fhirpath.evaluate` is loosely typed — and it
   * carries five fields the seed has no value for (`fullName`, `lastUpdated`,
   * `isInactive`, `external_referral_checkbox`, `mrnNumber`).
   *
   * So the header renders the seed's shape before first load and `FlatPatient`'s
   * after, and consumers reading `flatPatient.memberId[0]` as a string are right
   * only for the first. Reconciling the two models is app-wide; the state is
   * declared as the seed here, which is what the header prop already expects.
   */
  const [flatPatient, setFlatPatient] = useState<PatientCDOModel>(PatientCDO);
  const practlist = props.practlist;
  const vendorList = props.foodVendor;
  const [referralIdList, setReferralIdList] = useState<string[]>([]); //used for referral id dropdown
  // Set from a fhirpath `.toString()`, which is always a string; never reset.
  const [consultType, setConsultType] = useState<string | null>(null);
  const [consultTopic, setConsultTopic] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null); // Track when user selects cancelled
  const [showConsultInProgressWarning, setShowConsultInProgressWarning] = useState(false);
  const [showConsultCancelDueDateWarning, setShowConsultCancelDueDateWarning] = useState(false);
  const [pendingConsultDueDateStatus, setPendingConsultDueDateStatus] = useState<
    Task['status'] | null
  >(null);

  useEffect(() => {
    setLoader(true);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection leaves the loader spinning with no error shown
    tasksMutate().then(() => {
      setLoader(false);
    });
  }, [props.render, props.taskList]);

  useEffect(() => {
    // Task changed → reset transient UI state
    setErrorMessage('');
    setPendingStatus(null);
    setShowConsultInProgressWarning(false);
    setShowConsultCancelDueDateWarning(false);
    setPendingConsultDueDateStatus(null);
  }, [props.taskId]);

  const taskOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    // NO `if (!data) return;`. Upstream reads `data.groupIdentifier` unguarded, so an absent
    // payload throws there; an early return here survives type erasure and would silently leave
    // the setters below unrun, showing a stale task instead of failing. `FHIRFetcher` does resolve
    // `undefined`, so this is reachable — which is why the throw is the behaviour to keep.
    //
    // Asserted rather than narrowed at the parameter: SWR types `onSuccess`'s first argument as
    // `Task | undefined` and rejects a narrower one. `!` erases; the guard did not.
    onSuccess: (data: Task | undefined) => {
      const rid = data!.groupIdentifier ? data!.groupIdentifier.value : '';
      setReferralId(rid);

      const orderId = fhirpath
        .evaluate(
          data,
          "identifier.where(system='https://projectwell.io/fhir/identifiers/order-id').value",
        )
        ?.toString();
      setOrderId(orderId);
      const foodRecomNote = fhirpath
        .evaluate(
          data,
          "identifier.where(system='https://projectwell.io/fhir/identifiers/food-recommender-note').value",
        )
        ?.toString();
      setFoodRecommendationNotes(foodRecomNote);

      const consultTaskSubType = fhirpath
        .evaluate(
          data,
          "identifier.where(system='https://projectwell.io/fhir/identifiers/task-sub-type').value",
        )
        ?.toString();
      setConsultType(consultTaskSubType);

      const consultTopicValue = fhirpath
        .evaluate(
          data,
          "identifier.where(system='https://projectwell.io/fhir/identifiers/consult-topic').value",
        )
        ?.toString();
      setConsultTopic(consultTopicValue || null);
    },
  };
  const VMOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: VoiceMailAudioResponse | undefined) => {
      setAudioError('');
      if (data?.data === null) {
        setAudioError('Something went wrong');
      }
    },
  };

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    // Unguarded, matching upstream — see `taskOptions` above.
    onSuccess: (data: Patient | undefined, _key: string, _config: unknown) => {
      // The two-model disagreement recorded at `flatPatient`'s declaration: this is
      // a `FlatPatient` and the state is declared as the seed's `PatientCDOModel`.
      setFlatPatient(FHIRUtility.parseFHIRPatient(data!) as unknown as PatientCDOModel);
    },
  };
  const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
  const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

  /*
   * The key is `/Task/{id}`, so the payload is a FHIR Task.
   *
   * `Data` has to admit `undefined`: `FHIRFetcher` resolves `undefined` without
   * calling the API when handed an empty endpoint, so `Fetcher<Task>` — which
   * demands `Task | Promise<Task>` — does not describe it. Naming
   * `Task | undefined` is what lets the fetcher and the config agree on one
   * `Data`; `useSWR<Task>` fails here for exactly that reason.
   */
  const { data: tasksData, mutate: tasksMutate } = useSWR<Task | undefined>(
    props.taskId && [`/Task/` + props.taskId, authInfo.token],
    FHIRFetcher,
    taskOptions,
  );

  /** Care plan task list + single-task detail + patient-level task search (used by questionnaires). */
  const revalidateAllTaskCaches = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    props.tasksMutate();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    tasksMutate();
    void props.patientTasksMutate?.();
  };

  /** Task list + patient bundle (e.g. cancel, notes) without refetching the open task resource. */
  const revalidateCarePlanListAndPatientBundle = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    props.tasksMutate();
    void props.patientTasksMutate?.();
  };

  /*
   * The open task.
   *
   * `tasksData` is `Task | undefined` — the fetch has not resolved, or no task is
   * selected. The component renders its whole body behind `{taskpayload && …}` and
   * shows a `Dimmer` otherwise, so every field read and every mutating handler
   * below is reachable only once this is present.
   *
   * The declared type keeps that structural guarantee rather than restating it at
   * ~90 individual reads: `taskpayload` is the narrowed value, and the one place
   * the absence is handled is the `isLoading` / gate pair at the bottom of the
   * component, which tests `tasksData` directly.
   */
  let taskpayload = tasksData as Task;
  //TODO move this patient id fetch to a helper function..
  // This `&&` chain yields `false` when the task has no `for.reference`, and
  // `undefined` when the reference has no second segment — yet every
  // ProvenanceBuilder.build call site below declares this parameter as a
  // required `string`. The annotation records what is actually passed;
  // converting the falsy arms to '' would change what lands in the Provenance.
  const patientId: string = (taskpayload &&
    taskpayload.for &&
    taskpayload.for.reference &&
    taskpayload.for.reference.split('/').length > 1 &&
    taskpayload.for.reference.split('/')[1]) as string;

  const taskHelper = new Helpers.TaskHelper(taskpayload);
  //Voicemail tasks need to fetch audio recording
  // `undefined` for any task carrying no contact-id identifier; only interpolated
  // into the voicemail-audio key below, which is gated on `hasVM`.
  const contactId: string | undefined = taskHelper.getConnectId();
  const hasVM: boolean = taskHelper.isVMTask(); //voicemail tasks
  /*
   * SWR's `Key` is `string | any[] | null`, so the `false` this `&&` chain yields
   * when the task is not a voicemail has to become `null` — the value SWR reads as
   * "no key, do not fetch". Both are falsy and SWR skips on either, so this is the
   * same fetch decision spelled in the type SWR declares.
   */
  const { data: vmData, mutate: _VMTaskMutate } = useSWR<VoiceMailAudioResponse | undefined>(
    props.taskId && hasVM ? [`/hooks/fetch-voice-mail-audio/${contactId}`, authInfo.token] : null,
    ConnectHooksFetcher,
    VMOptions,
  );
  const vmURL: string | null = vmData?.data ?? null;
  // The key is `/Patient/{id}`, so the payload is a FHIR Patient; without the
  // generic, SWR infers `{}` from the fetcher and every field read below is an
  // error. `| undefined` for the same reason as the `/Task/{id}` call above.
  const { data: patientData, error: patientError } = useSWR<fhir4.Patient | undefined, Error>(
    patientId && [`/Patient/${patientId}`, authInfo.token],
    FHIRFetcher,
    options,
  );

  useEffect(() => {
    setLoader(true);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection leaves the loader spinning with no error shown
    referralDataMutate().then(() => {
      setLoader(false);
    });
  }, [props.taskId, patientId]);

  const referralOption = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Bundle | undefined) => {
      if (data && data.entry && data.entry.length > 0) {
        const tempReferralIdList: string[] = [];
        /*
         * Every entry, not just those whose `resource.resourceType` is
         * `Observation`: the key is an `/Observation/` search, but the resource is
         * reached through optional chaining and a resourceType-filtering helper
         * here would drop an entry that carries the identifier without declaring
         * its type.
         */
        (data.entry as ResourceEntry<'Observation'>[]).forEach((observation) => {
          const referralId = observation?.resource?.identifier?.find(
            (obj: Identifier) =>
              obj.system === 'https://projectwell.io/fhir/identifiers/referral-id',
          )?.value;
          if (referralId) {
            tempReferralIdList.push(referralId);
          }
        });
        setReferralIdList(tempReferralIdList);
      } else {
        setReferralIdList([]);
      }
    },
  };

  //fetch referral data
  const {
    data: _referralData,
    error: _referralError,
    mutate: referralDataMutate,
  } = useSWR<fhir4.Bundle, Error>(
    (props?.patientId || patientId) && [
      `/Observation/?patient=${props?.patientId || patientId}&code=${REFERRAL_OBSERVATION_CODE}`,
      authInfo.token,
    ],
    // `FHIRFetcher` resolves `Bundle | undefined` for an empty endpoint, while swr
    // 0.5.7's fetcher parameter declares no `undefined`.
    FHIRFetcher as () => Promise<fhir4.Bundle>,
    referralOption,
  );

  const patientName = (
    <>
      {!patientData && !patientError && <Loader active inline />}{' '}
      {patientData &&
        !patientError &&
        patientData.name &&
        // The `&&` tests that `name` exists but not that it is non-empty, and
        // `given` is optional — a patient with a name entry carrying no `given`
        // throws while rendering this header.
        patientData.name[0]!.given![0] + ' ' + patientData.name[0]!.family}{' '}
      {patientError && 'Error Loading Data'}
    </>
  );

  /*
   * The org id from `Patient.managingOrganization.reference` ("Organization/XYZ").
   *
   * Keeps upstream's `&&` chain rather than `?.`. The falsy arms are NOT
   * interchangeable here: `patientData` or `managingOrganization` being `null`, or
   * `reference` being `''`, makes the `&&` chain yield `null` / `''` where `?.`
   * yields `undefined`. That value flows into
   * `orgRef = { code: orgCode && ..., display: orgCode && ... }`, which
   * `ProvenanceBuilder.build` serialises into a Provenance POSTed to the FHIR proxy —
   * and `{"code":null}` and `{}` are different request bodies. Not a falsy-equivalence.
   */
  const patientOrg =
    patientData &&
    patientData.managingOrganization &&
    patientData.managingOrganization.reference &&
    patientData.managingOrganization.reference.split('/')[1];

  const changeStatus = async (
    newStatus: Task['status'],
    bypassConsultInProgressWarning = false,
    bypassConsultCancelDueDateWarning = false,
    memberEventDate?: string,
  ) => {
    const taskCode = taskHelper.getCode();
    const isEnrollmentAction = taskCode?.[0]?.code === 'enrollment-action';

    // Validate that statusReason is required when changing to cancelled for enrollment-action tasks only
    if (newStatus === 'cancelled' && isEnrollmentAction) {
      const currentStatusReason = taskpayload?.statusReason?.text;
      if (!currentStatusReason || currentStatusReason.trim() === '') {
        setLoader(false);
        setErrorMessage('Please select a status reason before cancelling this task.');
        setPendingStatus('cancelled'); // Show statusReason field
        // Don't proceed with status change - keep current status
        return;
      }
      // Clear pending status once validation passes
      setPendingStatus(null);
    } else {
      // Clear pending status if changing to a different status
      setPendingStatus(null);
    }

    if (
      !bypassConsultInProgressWarning &&
      needsConsultInProgressConfirmSingle(taskpayload, newStatus)
    ) {
      setShowConsultInProgressWarning(true);
      return;
    }

    if (
      !bypassConsultCancelDueDateWarning &&
      needsConsultCancelDueDateConfirmSingle(taskpayload, newStatus)
    ) {
      setPendingConsultDueDateStatus(newStatus);
      setShowConsultCancelDueDateWarning(true);
      return;
    }

    setShowConsultInProgressWarning(false);
    setShowConsultCancelDueDateWarning(false);
    setPendingConsultDueDateStatus(null);
    //e.preventDefault()
    setLoader(true);
    const oldTaskStatus = taskpayload.status;
    taskpayload.status = newStatus; //e.target.textContent.toLowerCase();
    // Don't clear statusReason if changing to cancelled - it's required
    if (taskpayload?.statusReason && newStatus !== 'cancelled') {
      taskpayload.statusReason = {
        text: '',
      };
    }
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };

    // FFS-MNT consult failed/cancelled: set due date to member event date in the same write as status
    if (
      isFfsMntConsultationTask(taskpayload) &&
      ['cancelled', 'failed'].includes(taskpayload.status) &&
      memberEventDate
    ) {
      applyConsultMemberEventDueDate(taskpayload, memberEventDate);
    } else if (
      (taskCode?.[0]?.code === 'careplan' || taskCode?.[0]?.code === 'enrollment-action') &&
      (taskpayload?.reasonCode?.text === 'intervention' ||
        taskpayload?.reasonCode?.text === 'enrollment') &&
      ['cancelled', 'completed'].includes(taskpayload.status)
    ) {
      //if intervention or enrollment task is marked as cancelled/completed, update due date to today
      // `executionPeriod` is optional on a FHIR Task and this file is
      // inconsistent about it — the comparison on the next line optional-chains
      // while the surrounding writes do not. A task served without an
      // executionPeriod therefore throws on these assignments. Recorded, not
      // handled: creating the object here would start persisting a period on
      // tasks that deliberately have none.
      taskpayload.executionPeriod!.end = moment().format('YYYY-MM-DD');
      if ((taskpayload.executionPeriod!.start ?? '') > moment().format('YYYY-MM-DD')) {
        taskpayload.executionPeriod!.start = moment().format('YYYY-MM-DD');
      }
    } else if (
      // An absent code really does reach `includes`, which simply returns false.
      // The cast keeps that; a `?? ''` would too, but this states the fact
      // rather than inventing a sentinel value.
      ['food-delivery', 'other-goods-delivery'].includes(
        taskpayload?.code?.coding?.[0]?.code as string,
      ) &&
      taskpayload.status === 'completed'
    ) {
      //update the food delivery task due date to today if status is marked as completed so we use that to query and create phone only hanlder in task-betty
      taskpayload.executionPeriod!.start = moment().format('YYYY-MM-DD');
      taskpayload.executionPeriod!.end = moment().format('YYYY-MM-DD');
    }

    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);

    const statusLabel =
      taskpayload.status === 'received' ? 'outreach' : taskpayload.status.toString();
    const provenanceActivity =
      isConsultationTask(taskpayload) && memberEventDate
        ? `Status has been changed to ${statusLabel} and due date set to ${memberEventDate} on ${taskpayload?.code?.coding?.[0]?.code} Task `
        : `Status has been changed to ${statusLabel} on ${taskpayload?.code?.coding?.[0]?.code} Task `;

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      provenanceActivity,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);

    await fhirProxyClient.send('post', '', fhirBundle).then(
      async (_response) => {
        //if intervention or enrollment task is marked as cancelled, update patient status
        if (
          (taskCode?.[0]?.code === 'careplan' || taskCode?.[0]?.code === 'enrollment-action') &&
          (taskpayload?.reasonCode?.text === 'intervention' ||
            taskpayload?.reasonCode?.text === 'enrollment') &&
          taskpayload.status === 'cancelled'
        ) {
          // Update all 'requested' and 'in-progress' tasks to 'cancelled' when intervention task is 'cancelled'
          const updateTasksNPatientStatusPayload = {
            patientId: patientId,
            status: 'INACTIVE',
            reason: 'DISENROLL',
            taskId: taskpayload.id,
          };

          await HooksPoster(
            `/hooks/patient/status`,
            authInfo.token,
            updateTasksNPatientStatusPayload,
          ).finally(() => {
            reloadTasks();
          });
        } else if (
          (taskCode?.[0]?.code === 'careplan' || taskCode?.[0]?.code === 'enrollment-action') &&
          (taskpayload?.reasonCode?.text === 'intervention' ||
            taskpayload?.reasonCode?.text === 'enrollment') &&
          taskpayload.status === 'in-progress' &&
          oldTaskStatus === 'cancelled'
        ) {
          // Update all not due 'cancelled' tasks to 'requested' when intervention task status is updated from 'cancelled' to 'in-progress'
          const updateTasksNPatientStatusPayload = {
            patientId: patientId,
            status: 'ACTIVE',
            reason: 'REENROLL',
            taskId: taskpayload.id,
          };

          await HooksPoster(
            `/hooks/patient/status`,
            authInfo.token,
            updateTasksNPatientStatusPayload,
          ).finally(() => {
            reloadTasks();
          });
        } else {
          reloadTasks();
        }
        // Clear pending status on successful status change
        setPendingStatus(null);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const reloadTasks = () => {
    revalidateAllTaskCaches();
    props.setPreviousURL(null);
    props.setRenderURL(true);
  };

  const onConsultTypeChange = (newConsultType: string) => {
    setLoader(true);
    const consultTypeIdentifier = taskpayload?.identifier?.find(
      (obj) => obj.system === 'https://projectwell.io/fhir/identifiers/task-sub-type',
    );
    const oldConsultType = consultTypeIdentifier?.value;
    if (consultTypeIdentifier) {
      consultTypeIdentifier.value = newConsultType;
    } else {
      // SUSPECTED DEFECT — REPORTED, NOT FIXED. `identifier` is `0..*` on a FHIR
      // Task, so a task served with no identifier array throws here and the
      // consult-type change is lost with a blank screen rather than a message.
      // Not fixed: seeding `identifier = []` would start persisting an identifier
      // array onto tasks that deliberately carry none.
      taskpayload.identifier!.push({
        system: 'https://projectwell.io/fhir/identifiers/task-sub-type',
        value: newConsultType,
      });
    }
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Consult type has been changed from ${oldConsultType ?? 'no consult type'} to ${newConsultType}
         on ${taskpayload?.code?.coding?.[0]?.code} Task `,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const onConsultTopicChange = (newConsultTopic: string) => {
    setLoader(true);
    const consultTopicIdentifier = taskpayload?.identifier?.find(
      (obj) => obj.system === 'https://projectwell.io/fhir/identifiers/consult-topic',
    );
    const oldConsultTopic = consultTopicIdentifier?.value;
    if (consultTopicIdentifier) {
      consultTopicIdentifier.value = newConsultTopic;
    } else {
      // Same optional-identifier exposure as in the consult-type handler above.
      taskpayload.identifier!.push({
        system: 'https://projectwell.io/fhir/identifiers/consult-topic',
        value: newConsultTopic,
      });
    }
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
      patientId,
      'UPDATE',
      `Consult topic has been changed from ${oldConsultTopic ?? 'no consult topic'} to ${newConsultTopic}
         on ${taskpayload?.code?.coding?.[0]?.code} Task `,
      orgRef,
    );
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  // Semantic's own signature, not intersected with `{ target: HTMLElement }`: that
  // intersection makes the attachment in `form/index.tsx` fail to match. The reads below
  // cast rather than narrow, so they stay unguarded property accesses.
  const changeRequest = (e: React.SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    setLoader(true);
    taskpayload.businessStatus = { text: (e.target as HTMLElement).textContent.toLowerCase() };
    // Same absent-executionPeriod exposure as in the status handler above.
    if (!isAutomatedTask(taskpayload) && !isFoodDeliveryTask(taskpayload)) {
      taskpayload.executionPeriod!.start = moment(taskpayload.executionPeriod!.start).format(
        'YYYY-MM-DD',
      );
      taskpayload.executionPeriod!.end = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
    } else {
      taskpayload.executionPeriod!.start = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
      taskpayload.executionPeriod!.end = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
    }
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Request has been changed to ` +
        (e.target as HTMLElement).textContent.toLowerCase() +
        ` on ${taskpayload?.code?.coding?.[0]?.code} Task `,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const changePriority = (newPrio: Task['priority']) => {
    //e.preventDefault();
    const oldPrio = taskpayload.priority;
    setLoader(true);
    taskpayload.priority = newPrio;
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Priority has been changed from ${oldPrio ?? 'no priority'} to ${newPrio}
         on ${taskpayload?.code?.coding?.[0]?.code} Task `,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const changeVendor = (e: React.SyntheticEvent<HTMLElement>, dropdownprops: DropdownProps) => {
    setLoader(true);
    e.preventDefault();
    taskpayload.restriction = {
      recipient: [
        {
          reference: 'Organization/' + dropdownprops.value!.toString(),
          display: VENDOR_MAP(vendorList)?.[dropdownprops.value!.toString()],
        },
      ],
    };
    //find identifier, change it if present, otherwise add it
    const identifiersList = taskpayload.identifier ? taskpayload.identifier : [];
    const vendorIdentifierIndex = identifiersList.findIndex((identifier) => {
      return identifier.system!.toString().includes('vendor-codes');
    });
    if (vendorIdentifierIndex === -1) {
      identifiersList.push({
        system: 'https://projectwell.io/fhir/identifiers/vendor-codes',
        value: dropdownprops.value!.toString(),
      });
    } else {
      // `vendorIdentifierIndex` came from findIndex and is != -1 in this branch.
      identifiersList[vendorIdentifierIndex]!['system'] =
        'https://projectwell.io/fhir/identifiers/vendor-codes';
      identifiersList[vendorIdentifierIndex]!['value'] = dropdownprops.value!.toString();
    }
    taskpayload.identifier = identifiersList;
    // Same absent-executionPeriod exposure as in changeRequest above.
    if (!isAutomatedTask(taskpayload) && !isFoodDeliveryTask(taskpayload)) {
      taskpayload.executionPeriod!.start = moment(taskpayload.executionPeriod!.start).format(
        'YYYY-MM-DD',
      );
      taskpayload.executionPeriod!.end = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
    } else {
      taskpayload.executionPeriod!.start = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
      taskpayload.executionPeriod!.end = moment(taskpayload.executionPeriod!.end).format(
        'YYYY-MM-DD',
      );
    }
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Vendor has been changed to ` +
        VENDOR_MAP(vendorList)?.[dropdownprops.value!.toString()] +
        ` on ${taskpayload?.code?.coding?.[0]?.code} Task`,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const changeAssignee = (e: React.SyntheticEvent<HTMLElement>, dropdownprops: DropdownProps) => {
    setLoader(true);
    e.preventDefault();
    taskpayload.owner = {
      reference: 'Practitioner/' + dropdownprops.value!.toString(),
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. The Dropdown's options ARE
       * `practlist`, built from these same `flatPractitioners`, so a selection
       * always matches — but the value is compared with `===` against
       * `FlatPractitioner.id` (a string) while `dropdownprops.value` is
       * `DropdownProps['value']`. Any selection that does not match yields an
       * empty array and `[0]` is `undefined`, so `.fullName` throws inside the
       * assignee handler. The `!` records the reach the original made.
       */
      display: flatPractitioners.filter((x) => {
        return x.id === dropdownprops.value;
      })[0]!.fullName,
    };
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Assignee has been changed to ` +
        // `owner` and its `display` are both optional and read unguarded here.
        taskpayload.owner!.display!.toString() +
        ` on ${taskpayload?.code?.coding?.[0]?.code} Task`,
      orgRef,
    );
    let fhirBundle: fhir4.Bundle;
    //do not create flag if a task is food delivery or consultation task
    if (
      !['consultation', 'food-delivery'].includes(taskpayload?.code?.coding?.[0]?.code as string)
    ) {
      const flagpayload: Flag = FlagBuilder.build(
        patientId,
        authInfo.practitionerId,
        { code: 'admin', display: 'administrative' },
        { code: 'ALERT', display: 'Alert' },
        // `code`, `coding`, `coding[0].code`, `owner` and `owner.reference` are
        // all optional and all read unguarded on this Flag-building path, unlike
        // the `taskpayload?.code?.coding?.[0]?.code` form used elsewhere in this
        // same file.
        taskpayload.code!.coding![0]!.code!.toString().toUpperCase() +
          ' related task assigned to you by ' +
          authInfo.practitionerName,
        authInfo.practitionerName,
        // Same absence as `patientOrganizationId` in `./cancel`: `undefined` for a
        // patient with no managing organization, read unguarded by the builder.
        orgCode!,
        taskpayload.owner!.reference!.split('/')[1],
        'active',
        taskpayload.id,
      );
      //now post bundle request
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload, flagpayload], provenance);
    } else {
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    }

    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };
  const handleChangeDate = (
    e: React.SyntheticEvent<HTMLElement> | undefined,
    semanticprops: { value: Date | null },
  ) => {
    if (
      semanticprops.value === null ||
      moment(taskpayload.executionPeriod!.end ?? taskpayload.executionPeriod!.start).format(
        'YYYY-MM-DD',
      ) === moment(semanticprops.value).format('YYYY-MM-DD')
    ) {
      return false;
    }
    if (!isAutomatedTask(taskpayload) && !isFoodDeliveryTask(taskpayload)) {
      if (
        moment(semanticprops.value).format('YYYY-MM-DD') <
          moment(taskpayload.meta!.lastUpdated).format('YYYY-MM-DD') &&
        moment(semanticprops.value).format('YYYY-MM-DD') <
          moment(taskpayload.executionPeriod?.start).format('YYYY-MM-DD') &&
        moment(semanticprops.value).format('YYYY-MM-DD') <
          moment(taskpayload.executionPeriod?.end).format('YYYY-MM-DD')
      ) {
        //if the selected new due date is smaller than last updated date and both executionPeriod, then we will update the executionPeriod to selected new due date
        // The comparisons just above optional-chain `executionPeriod`; these
        // writes do not. Same absent-executionPeriod exposure as elsewhere.
        taskpayload.executionPeriod!.start = moment(semanticprops.value).format('YYYY-MM-DD');
        taskpayload.executionPeriod!.end = moment(semanticprops.value).format('YYYY-MM-DD');
      } else if (
        moment(taskpayload.executionPeriod?.start).format('YYYY-MM-DD') >
        moment(semanticprops.value).format('YYYY-MM-DD')
      ) {
        //if both start date and last updated date is greater than the current new due date, then we will set start date as today. Otherwise, we will mark last updated date as start date
        // `meta` is optional too and is read unguarded here.
        if (
          moment(taskpayload.meta!.lastUpdated).format('YYYY-MM-DD') >
          moment(semanticprops.value).format('YYYY-MM-DD')
        ) {
          taskpayload.executionPeriod!.start = moment().format('YYYY-MM-DD');
        } else {
          taskpayload.executionPeriod!.start = moment(taskpayload.meta!.lastUpdated).format(
            'YYYY-MM-DD',
          );
          taskpayload.executionPeriod!.end = moment(semanticprops.value).format('YYYY-MM-DD');
        }
      } else {
        taskpayload.executionPeriod!.start = moment(taskpayload.executionPeriod!.start).format(
          'YYYY-MM-DD',
        );
        taskpayload.executionPeriod!.end = moment(semanticprops.value).format('YYYY-MM-DD');
      }
    }
    //set both execution period the same date for automated tasks
    else if (isAutomatedTask(taskpayload) || isFoodDeliveryTask(taskpayload)) {
      taskpayload.executionPeriod = {
        end: moment(semanticprops.value).format('YYYY-MM-DD'),
        start: moment(semanticprops.value).format('YYYY-MM-DD'),
      };
    }
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Task due date has been changed to ` +
        taskpayload.executionPeriod!.end +
        ` on ${taskpayload?.code?.coding?.[0]?.code} Task`,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    setLoader(true);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const handleReferralId = (referralId: string) => {
    setLoader(true);
    taskpayload.groupIdentifier = {
      system: 'https://projectwell.io/fhir/identifiers/referral-id',
      value: referralId,
    };
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Referral Id has been changed to ${referralId} on ${taskpayload?.code?.coding?.[0]?.code} Task`,
      orgRef,
    );

    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  const changeStatusReason = (e: React.SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    const statusReasonText = (e.target as HTMLElement).textContent.toLowerCase();
    taskpayload.statusReason = { text: statusReasonText };

    // If we have a pending cancelled status and now have a reason, automatically change status to cancelled
    if (pendingStatus === 'cancelled' && taskpayload.status !== 'cancelled') {
      // Clear error message and pending status
      if (errorMessage) {
        setErrorMessage('');
      }
      setPendingStatus(null);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      changeStatus('cancelled');
      return;
    }

    // Normal statusReason change (not auto-changing to cancelled)
    setLoader(true);
    const orgCode = patientOrg && patientOrg;
    const orgRef = {
      code: orgCode && orgCode.toUpperCase(),
      display: orgCode && orgCode.toLowerCase(),
    };
    taskpayload = FHIRUtils.addUpdatedBy(taskpayload, authInfo.practitionerName);
    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Task/${taskpayload.id!}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName }, //eg. {Practitioner/oa2,johnsmith},
      patientId,
      'UPDATE',
      `Status Reason has been changed to ${statusReasonText} on ${taskpayload?.code?.coding?.[0]?.code} Task`,
      orgRef,
    );
    //now post bundle request
    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([taskpayload], provenance);
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        revalidateAllTaskCaches();
        props.setPreviousURL(null);
        props.setRenderURL(true);
      },
      (response: FhirSendRejection) => {
        setLoader(false);
        setErrorMessage(response.toJSON()['message']);
      },
    );
  };

  function copyToClipBoard(id: string) {
    const copyText = document.getElementById(id) as HTMLInputElement;
    // // Select the text field
    // copyText.select();
    // Copy the text inside the text field
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget clipboard write; a rejection leaves the clipboard unchanged while the tick still renders
    navigator.clipboard.writeText(copyText?.value);
    setClipBoard('orderId' + orderId); // Changing Icon to Check
  }

  const numTasks = props.taskList && props.taskList.length;

  //check if a task is careplan task
  const isCareplan = taskpayload?.code?.coding?.[0]?.code === 'careplan';

  const isLoading = loader || Boolean(!tasksData && props.taskId);

  return (
    <>
      <Dimmer active={isLoading} inverted>
        <Loader inverted />
      </Dimmer>

      {tasksData && (
        <>
          <ConsultInProgressWarning
            open={showConsultInProgressWarning && isConsultationTask(taskpayload)}
            onDismiss={() => setShowConsultInProgressWarning(false)}
            onConfirm={() => {
              setShowConsultInProgressWarning(false);
              void changeStatus('in-progress', true);
            }}
          />
          <ConsultCancelDueDateWarning
            open={showConsultCancelDueDateWarning && isConsultationTask(taskpayload)}
            targetStatus={pendingConsultDueDateStatus}
            onDismiss={() => {
              setShowConsultCancelDueDateWarning(false);
              setPendingConsultDueDateStatus(null);
            }}
            onConfirm={(memberEventDate) => {
              const statusToApply = pendingConsultDueDateStatus ?? 'cancelled';
              setShowConsultCancelDueDateWarning(false);
              setPendingConsultDueDateStatus(null);
              void changeStatus(statusToApply, false, true, memberEventDate);
            }}
          />
        </>
      )}

      {tasksData && numTasks !== null && numTasks > 0 && !isLoading && (
        <Grid container>
          <TaskHeader
            activity={props.activity}
            flatPatient={flatPatient}
            onPatientSelected={props.onPatientSelected!}
            patientId={patientId}
            patientName={patientName}
            patientOrg={patientOrg}
            setLoader={setLoader}
            loader={loader}
            setErrorMessage={setErrorMessage}
            taskPayload={taskpayload}
            tasksMutate={revalidateCarePlanListAndPatientBundle}
          />
          <Segment style={{ width: '100%' }}>
            <TaskDetailsForm
              taskpayload={taskpayload}
              taskHelper={taskHelper}
              vmURL={vmURL}
              audioError={audioError}
              referralId={referralId}
              orderId={orderId}
              clipBoard={clipBoard}
              practlist={practlist}
              vendorList={vendorList}
              referralIdList={referralIdList}
              errorMessage={errorMessage}
              isCareplan={isCareplan}
              consultType={consultType}
              consultTopic={consultTopic}
              onDateChange={handleChangeDate}
              onAssigneeChange={changeAssignee}
              onPriorityChange={changePriority}
              onReferralIdChange={handleReferralId}
              onStatusChange={changeStatus}
              onVendorChange={changeVendor}
              onRequestChange={changeRequest}
              onStatusReasonChange={changeStatusReason}
              onCopyToClipboard={copyToClipBoard}
              onErrorMessageDismiss={() => setErrorMessage('')}
              onConsultTypeChange={onConsultTypeChange}
              onConsultTopicChange={onConsultTopicChange}
              pendingStatus={pendingStatus}
              onPendingStatusChange={setPendingStatus}
            />

            <TaskDetailTabs
              taskpayload={taskpayload}
              foodRecommendationNotes={foodRecommendationNotes}
              orderId={orderId}
              flatPractitioners={props.flatPractitioners}
              // Cast, not adapted: the prop declares the SWR mutate signature and this
              // installs the same zero-arg callback as before.
              refresh={
                revalidateCarePlanListAndPatientBundle as unknown as React.ComponentProps<
                  typeof TaskDetailTabs
                >['refresh']
              }
              patientId={patientData?.id}
              orgId={patientOrg}
              setLoader={setLoader}
              loader={loader}
              setRenderURL={props.setRenderURL}
            />
          </Segment>
        </Grid>
      )}
      {(!props.taskId || numTasks === 0) && (
        <Container as="h4" textAlign="center" style={{ opacity: '70%' }}>
          Please Select a Task
        </Container>
      )}
    </>
  );
};

export default TaskDetails;
